import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { ExternalLink } from "lucide-react"
import PublicLayout from "@/components/PublicLayout"
import { TherapistAvatar } from "@/components/ui/therapist-avatar"
import { LocationTag } from "@/components/ui/location-tag"
import type { PageProps } from "@/types/inertia"

interface Therapist {
  id: number
  name: string
  slug: string
  speciality: string
  bio: string | null
  photo_url: string | null
  website_url: string | null
  listed_at: string | null
  sites: { name: string; slug: string }[]
}

interface Props {
  therapist: Therapist
}

export default function TherapistShow({ therapist }: Props) {
  const { translations } = usePage<PageProps>().props
  const t = translations.therapists.show

  return (
    <>
      <Head title={`${therapist.name} — Sanctuary Therapy Centre`}>
        <meta
          name="description"
          content={`${therapist.name} — ${therapist.speciality}. Practising at Sanctuary Therapy Centre.${therapist.sites.length > 0 ? ` Based at ${therapist.sites.map((s) => s.name).join(" and ")}.` : ""}`}
        />
        <meta property="og:title" content={`${therapist.name} — Sanctuary Therapy Centre`} />
        <meta
          property="og:description"
          content={`${therapist.name} — ${therapist.speciality} at Sanctuary Therapy Centre.`}
        />
      </Head>
      <PublicLayout>
        <section className="bg-stone pt-32 pb-16">
          <div className="mx-auto max-w-3xl px-6 lg:px-8">
            <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
              {therapist.photo_url ? (
                <img
                  src={therapist.photo_url}
                  alt={therapist.name}
                  className="h-28 w-28 shrink-0 rounded-full object-cover shadow-md"
                />
              ) : (
                <TherapistAvatar name={therapist.name} size="lg" className="shrink-0" />
              )}
              <div>
                <h1>{therapist.name}</h1>
                <p className="mt-1 text-base text-ink-muted">{therapist.speciality}</p>
                {therapist.sites.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2 sm:justify-start justify-center">
                    {therapist.sites.map((site) => (
                      <LocationTag key={site.slug} name={site.name} />
                    ))}
                  </div>
                )}
                {therapist.website_url && (
                  <a
                    href={therapist.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-teal-mid hover:text-teal"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t.visit_website}
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Bio */}
        <section className="bg-cream py-14">
          <div className="mx-auto max-w-3xl px-6 lg:px-8">
            {therapist.bio ? (
              <div className="body-content prose max-w-none text-ink-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{therapist.bio}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-ink-muted">{t.bio_coming_soon}</p>
            )}

            <div className="mt-10 border-t border-hairline pt-8">
              <a href="/therapists" className="text-sm font-medium text-teal-mid hover:text-teal">
                {t.back_link}
              </a>
            </div>
          </div>
        </section>

        <section className="bg-stone py-16">
          <div className="mx-auto max-w-3xl px-6 text-center lg:px-8">
            <h2>Interested in working with {therapist.name}?</h2>
            <p className="mt-4 text-ink-muted">Get in touch to discuss how we can help.</p>
            <a href="/contact" className="mt-6 inline-block rounded-full bg-teal px-6 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90">
              Get in touch →
            </a>
          </div>
        </section>
      </PublicLayout>
    </>
  )
}
